import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { compressImage } from "../lib/imageCompress";
import { Tag, Wrench, CheckCircle, ArrowLeft, WarningCircle, Sticker, Camera, Bank, X as XIcon, MagnifyingGlass } from "@phosphor-icons/react";

// Builds a homeowner-friendly Google search URL. Google surfaces AI
// Overviews on questions like this for free, so the homeowner can read
// possible causes + DIY checks while the artisan is en route. We never
// hit a paid LLM API — the "AI" is whatever Google chooses to show on
// that public search page. Costs nothing per query.
function diyChecksUrl(item, description) {
  const what = (item || "appliance").trim();
  const desc = (description || "").trim();
  const q = desc
    ? `What could cause a ${what} to ${desc}? Common DIY checks a homeowner can do before calling a technician.`
    : `Common faults with a ${what} and DIY checks a homeowner can do before calling a technician.`;
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

// Shared button for the "Possible causes & DIY checks" CTA. Renders the
// same on the public Asset page (pre-submit) and on the post-submit
// "Reported" success block — see Asset.jsx + FaultReportForm.
function DiyChecksButton({ item, description, testid, variant = "primary" }) {
  const href = diyChecksUrl(item, description);
  const cls = variant === "primary"
    ? "inline-flex items-center justify-center gap-2 w-full bg-black text-white border-2 border-black px-4 py-3 brutal-btn label-tag tracking-wider"
    : "inline-flex items-center justify-center gap-2 w-full bg-white text-black border-2 border-black px-4 py-3 brutal-btn label-tag tracking-wider";
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>
      <MagnifyingGlass size={16} weight="bold" /> Possible causes &amp; DIY checks
    </a>
  );
}

// Public asset page — what a visitor sees after scanning a secondary asset
// sticker (e.g. on a geyser, vehicle, equipment). They see the asset
// details, a "Visit cardholder card" link, and a fault-report form that
// emails the cardholder. No login required.
export default function Asset() {
  const { assetCode } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true });
  const [reporterName, setReporterName] = useState("");
  const [reporterPhone, setReporterPhone] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reported, setReported] = useState(false);
  const [error, setError] = useState("");
  // Photo upload state for the fault report.
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  // Claim flow state — for retail-pack stickers that haven't been bound yet.
  const [claimError, setClaimError] = useState("");
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/asset/${assetCode}`);
        setState({
          loading: false,
          asset: data.asset,
          cardholder: data.cardholder,
          homeowner: data.homeowner,
          serviceEvents: data.service_events_public || [],
        });
      } catch (e) {
        setState({ loading: false, error: formatApiError(e) });
      }
    })();
  }, [assetCode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reporterName.trim()) return setError("Your name is required");
    if (!/^\d{7,15}$/.test(reporterPhone.replace(/\D/g, ""))) return setError("A working phone number is required");
    if (description.trim().length < 5) return setError("Please describe the fault");
    setSubmitting(true);
    try {
      await api.post(`/public/asset/${assetCode}/fault`, {
        reporter_name: reporterName,
        reporter_phone: reporterPhone,
        description,
        photo_url: photoUrl,
      });
      setReported(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  // Compress + upload a fault photo. Reuses the existing client-side
  // compressor so a 5MB phone photo lands at ~150KB before upload.
  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true); setError("");
    try {
      const compressed = await compressImage(file, { maxDim: 1600, quality: 0.78 });
      const fd = new FormData();
      fd.append("file", compressed, file.name || "fault.jpg");
      const { data } = await api.post(`/public/asset/${assetCode}/fault-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(data.url);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setPhotoUploading(false); }
  };

  if (state.loading) {
    return <div><Nav /><div className="px-4 py-20 text-center label-tag">Loading…</div></div>;
  }
  if (state.error) {
    return (
      <div>
        <Nav />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <WarningCircle size={48} weight="bold" className="mx-auto text-red-600" />
          <h1 className="font-display font-black text-2xl mt-3">Asset not found</h1>
          <p className="text-sm text-neutral-700 mt-2">This sticker may have been removed or the QR is damaged. {state.error}</p>
          <Link to="/" className="inline-block mt-6 label-tag px-4 py-2 border-2 border-black brutal-btn bg-white">Curb Crew home</Link>
        </div>
      </div>
    );
  }

  // Unbound retail-pack sticker — visitor scanned a brand-new sticker from a
  // pack of 10 that hasn't been claimed by any cardholder yet. Cardholder
  // logs in (or clicks "I have a card") and claims it; the page then jumps
  // to their dashboard with the new asset highlighted.
  if (state.asset && state.asset.status === "unbound") {
    const claim = async (chId) => {
      setClaimError(""); setClaiming(true);
      try {
        await api.post(`/cardholder/cards/${chId}/assets/claim`, { asset_code: state.asset.asset_code });
        nav("/my-cards?claimed=" + state.asset.asset_code);
      } catch (e) {
        setClaimError(formatApiError(e));
      } finally { setClaiming(false); }
    };
    return <UnboundStickerClaim asset={state.asset} user={user} claim={claim} claiming={claiming} claimError={claimError} />;
  }

  const { asset, cardholder, homeowner, serviceEvents = [] } = state;

  return (
    <div className="min-h-screen bg-[color:var(--cc-bg)]">
      <Nav />
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="label-tag text-neutral-500 mb-2">Curb Crew · Asset</div>
        <div className="bg-white border-2 border-black p-4">
          <div className="flex items-center gap-2 mb-2">
            <Tag size={20} weight="bold" />
            <div className="font-mono-cc text-xs text-neutral-500">#{asset.asset_code}</div>
          </div>
          <h1 className="font-display font-black text-2xl tracking-tight leading-tight" data-testid="asset-label">{asset.label}</h1>

          <dl className="mt-4 grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            {asset.item && (<><dt className="label-tag text-neutral-500 col-span-1">Item</dt><dd className="col-span-2">{asset.item}</dd></>)}
            {asset.make && (<><dt className="label-tag text-neutral-500 col-span-1">Make</dt><dd className="col-span-2">{asset.make}</dd></>)}
            {asset.model && (<><dt className="label-tag text-neutral-500 col-span-1">Model</dt><dd className="col-span-2">{asset.model}</dd></>)}
            {asset.serial && (<><dt className="label-tag text-neutral-500 col-span-1">Serial</dt><dd className="col-span-2 font-mono-cc text-xs">{asset.serial}</dd></>)}
          </dl>
          {asset.notes && (
            <div className="mt-4 border-t border-dashed border-neutral-400 pt-3">
              <div className="label-tag text-neutral-500 mb-1">Notes</div>
              <p className="text-sm text-neutral-800 whitespace-pre-wrap">{asset.notes}</p>
            </div>
          )}
          {asset.next_service_at && (
            <div className="mt-3 px-3 py-2 bg-[color:var(--cc-bg-2)] border border-black inline-flex items-center gap-2" data-testid="asset-next-service">
              <div className="label-tag text-neutral-700">Next service due:</div>
              <div className="font-mono-cc text-xs">{asset.next_service_at.slice(0, 10)}</div>
            </div>
          )}
        </div>

        {/* Service provider (the artisan) */}
        <div className="mt-4 bg-white border-2 border-black p-4">
          <div className="label-tag text-neutral-500 mb-2">Serviced by</div>
          <div className="flex items-center gap-3">
            {cardholder.picture_url && (
              <img
                src={cardholder.picture_url.startsWith("http") ? cardholder.picture_url : `${process.env.REACT_APP_BACKEND_URL}${cardholder.picture_url}`}
                alt=""
                className="w-14 h-14 object-cover border border-black"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-lg truncate" data-testid="asset-cardholder-name">{cardholder.full_name}</div>
              <div className="label-tag text-neutral-700 truncate">{(cardholder.skills || []).join(" · ")}</div>
            </div>
          </div>
          <Link
            to={`/c/${cardholder.code}`}
            className="mt-3 inline-flex items-center gap-2 label-tag px-3 py-2 border-2 border-black bg-[color:var(--cc-yellow)] brutal-btn text-xs"
            data-testid="asset-view-card-btn"
          >View {cardholder.full_name.split(" ")[0]}'s card →</Link>
        </div>

        {/* Homeowner block — only when the asset has been claimed by one. */}
        {homeowner && (
          <div className="mt-3 bg-white border-2 border-black p-3" data-testid="asset-homeowner-block">
            <div className="label-tag text-neutral-500 mb-1">Owned by</div>
            <div className="flex items-center justify-between gap-2">
              <div className="font-display font-black text-base truncate">{homeowner.full_name}</div>
              <Link to={`/c/${homeowner.code}`} className="label-tag text-[10px] px-2 py-1 border border-black bg-white brutal-btn">View card →</Link>
            </div>
          </div>
        )}

        {/* Curb Auto emergency block — public scan exposes ONLY the
            emergency context the backend already filters in to (insurance,
            tow, license expiry). Workshop-internal detail (VIN, tires_*,
            battery_*, customer_*, service log) is stripped server-side at
            /public/asset/<code>. Renders only when at least one of the
            emergency fields is populated on a curb_auto card. */}
        {cardholder.card_type === "curb_auto" &&
          (asset.insurance_phone || asset.tow_phone || asset.license_expiry || asset.insurance_company || asset.tow_company) && (
            <section
              className="mt-4 bg-[#fff4ec] border-2 border-black p-4"
              data-testid="asset-curb-auto-emergency"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="label-tag text-[10px] tracking-[0.25em] bg-black text-white px-2 py-1">
                  IF SOMETHING&apos;S WRONG
                </div>
                {asset.license_expiry && (
                  <div className="label-tag text-[10px] text-neutral-700">
                    Licence: <span className="font-mono-cc">{asset.license_expiry.slice(0, 10)}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(asset.insurance_company || asset.insurance_phone) && (
                  <a
                    href={asset.insurance_phone ? `tel:${asset.insurance_phone.replace(/\s+/g, "")}` : undefined}
                    className="bg-white border-2 border-black p-3 brutal-btn block"
                    data-testid="asset-curb-auto-insurance"
                  >
                    <div className="label-tag text-neutral-500">Insurance</div>
                    {asset.insurance_company && (
                      <div className="font-display font-black text-lg mt-1 leading-tight">
                        {asset.insurance_company}
                      </div>
                    )}
                    {asset.insurance_phone && (
                      <div className="font-mono-cc text-sm mt-1 underline">
                        {asset.insurance_phone}
                      </div>
                    )}
                    {asset.insurance_phone && (
                      <div className="label-tag text-[10px] text-neutral-600 mt-2">
                        Tap to call claim line
                      </div>
                    )}
                  </a>
                )}

                {(asset.tow_company || asset.tow_phone) && (
                  <a
                    href={asset.tow_phone ? `tel:${asset.tow_phone.replace(/\s+/g, "")}` : undefined}
                    className="bg-white border-2 border-black p-3 brutal-btn block"
                    data-testid="asset-curb-auto-tow"
                  >
                    <div className="label-tag text-neutral-500">Tow company</div>
                    {asset.tow_company && (
                      <div className="font-display font-black text-lg mt-1 leading-tight">
                        {asset.tow_company}
                      </div>
                    )}
                    {asset.tow_phone && (
                      <div className="font-mono-cc text-sm mt-1 underline">
                        {asset.tow_phone}
                      </div>
                    )}
                    {asset.tow_phone && (
                      <div className="label-tag text-[10px] text-neutral-600 mt-2">
                        Tap to call tow
                      </div>
                    )}
                  </a>
                )}
              </div>

              <div className="label-tag text-[10px] text-neutral-600 mt-3">
                Workshop service log, customer details and VIN are private to the shop.
              </div>
            </section>
          )}

        {/* Public service-event timeline (Feb 2026).
            Surfaces the asset's redacted work history so the homeowner
            who scans the kitchen DB-board sticker (or the new plumber
            who just took over from the old one) can see "Element
            replaced 14 Mar 2025 by Sipho Plumbing" at a glance. Notes,
            materials, cost, customer data and internal_only entries are
            stripped server-side. */}
        {serviceEvents.length > 0 && (
          <section
            className="mt-4 bg-white border-2 border-black p-4"
            data-testid="asset-service-history"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-black text-base">Work history</div>
              <div className="label-tag text-[10px] text-neutral-500">
                {serviceEvents.length} {serviceEvents.length === 1 ? "entry" : "entries"}
              </div>
            </div>
            <ol className="space-y-2">
              {serviceEvents.map((ev, i) => (
                <li
                  key={`svc-${i}`}
                  className="border-l-2 border-black pl-3 py-1"
                  data-testid={`asset-service-history-row-${i}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="label-tag text-[10px] bg-black text-white px-1.5 py-0.5 inline-block">
                      {ev.type || "service"}
                    </span>
                    <span className="font-mono-cc text-[11px] text-neutral-600 whitespace-nowrap">
                      {(ev.date || "").slice(0, 10)}
                    </span>
                  </div>
                  {ev.summary && (
                    <div className="mt-1 text-sm leading-snug">{ev.summary}</div>
                  )}
                  <div className="mt-1 label-tag text-[10px] text-neutral-600">
                    by {ev.author_name || "—"}
                  </div>
                </li>
              ))}
            </ol>
            <div className="label-tag text-[10px] text-neutral-500 mt-3">
              Notes, parts and cost stay private to the artisan and the homeowner.
            </div>
          </section>
        )}

        {/* Homeowner CTA — only relevant on service-style stickers, not Curb Crew (a car guard's pillar doesn't have a homeowner). */}
        {cardholder.card_type !== "curb_crew" && (
          <HomeownerCTA asset={asset} homeowner={homeowner} cardholder={cardholder} user={user} />
        )}

        {/* Curb Crew branch — if the cardholder is on a tips-first card and
            has PayShap on, show the Tip Jar instead of a fault report form.
            A car guard sticker on a parking spot wants tips, not "report fault". */}
        {cardholder.card_type === "curb_crew" && cardholder.pay_enabled && cardholder.pay_phone && (
          <AssetTipJar cardholder={cardholder} />
        )}

        {/* Fault report form — hidden on Curb Crew cards. */}
        {cardholder.card_type !== "curb_crew" && (
          reported ? (
            <div className="mt-4 bg-green-500 border-2 border-black p-4 text-center" data-testid="asset-fault-reported">
              <CheckCircle size={32} weight="bold" className="mx-auto" />
              <div className="font-display font-black text-xl mt-2">Reported</div>
              <p className="text-sm mt-1">{cardholder.full_name.split(" ")[0]} has been emailed and will get back to you on the number you gave.</p>
              {/* Free DIY-checks link — gives the homeowner something to
                  read while the artisan is en route. No cost to anyone:
                  it's just a pre-filled Google search that surfaces AI
                  Overviews and trusted how-to articles. */}
              <div className="mt-4 text-left">
                <div className="text-xs text-neutral-700 mb-2">Want to try something while you wait?</div>
                <DiyChecksButton item={asset.item} description={description} testid="asset-diy-checks-after" />
              </div>
            </div>
          ) : (
            <FaultReportForm
              submit={submit}
              cardholder={cardholder}
              asset={asset}
              reporterName={reporterName} setReporterName={setReporterName}
              reporterPhone={reporterPhone} setReporterPhone={setReporterPhone}
              description={description} setDescription={setDescription}
              photoUrl={photoUrl} setPhotoUrl={setPhotoUrl}
              photoUploading={photoUploading}
              uploadPhoto={uploadPhoto}
              submitting={submitting}
              error={error}
            />
          )
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="label-tag text-neutral-500 inline-flex items-center gap-1">
            <ArrowLeft size={12} weight="bold" /> Curb Crew
          </Link>
        </div>
      </div>
    </div>
  );
}

// Unbound sticker prompt — shown when a visitor scans a sticker that was
// in a retail pack but hasn't been claimed yet. Two branches:
//   1. Visitor already logged in as cardholder → show their cards as
//      "Claim to this card" tiles.
//   2. Visitor not logged in → show "Log in / Activate your card" CTA
//      and remind them they need a Curb Crew main card first.
function UnboundStickerClaim({ asset, user, claim, claiming, claimError }) {
  const [cards, setCards] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const isCardholder = !!user && user.role === "cardholder";

  React.useEffect(() => {
    if (!isCardholder) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/cardholder/cards");
        setCards((data.cards || []).filter((c) => !c.lost_reported && c.is_active !== false));
      } catch { setCards([]); }
      finally { setLoading(false); }
    })();
  }, [isCardholder]);

  return (
    <div className="min-h-screen bg-[color:var(--cc-bg)]">
      <Nav />
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="label-tag text-neutral-500 mb-2">Curb Crew · New sticker</div>
        <div className="bg-[color:var(--cc-yellow)] border-2 border-black p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sticker size={20} weight="bold" />
            <div className="font-mono-cc text-xs">#{asset.asset_code}</div>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight leading-tight">New sticker — not linked yet</h1>
          <p className="text-sm mt-3 leading-snug">
            This is a fresh Curb Crew asset sticker from a retail pack. Link it to your card to start using it — once linked, anyone who scans it will see what it's for and can report a fault straight to you.
          </p>
        </div>

        {/* Branch 1: logged-in cardholder picks which card to claim to */}
        {isCardholder && (
          <div className="mt-4 bg-white border-2 border-black p-4" data-testid="claim-as-cardholder">
            <div className="label-tag mb-2">Claim this sticker to:</div>
            {loading && <div className="text-sm text-neutral-500">Loading your cards…</div>}
            {!loading && cards && cards.length === 0 && (
              <div className="text-sm text-neutral-700">You don't have any active cards yet. Activate one first, then come back here.</div>
            )}
            {!loading && cards && cards.length > 0 && (
              <ul className="space-y-2" data-testid="claim-card-list">
                {cards.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={claiming}
                      onClick={() => claim(c.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 border-2 border-black bg-white brutal-btn text-left disabled:opacity-50"
                      data-testid={`claim-to-card-${c.code}`}
                    >
                      {c.picture_url && <img src={c.picture_url.startsWith("http") ? c.picture_url : `${process.env.REACT_APP_BACKEND_URL}${c.picture_url}`} alt="" className="w-10 h-10 object-cover border border-black flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-sm truncate">{c.full_name}</div>
                        <div className="font-mono-cc text-[10px] text-neutral-500">#{c.code}</div>
                      </div>
                      <div className="label-tag text-xs">{claiming ? "…" : "Claim →"}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {claimError && <div className="label-tag text-red-600 mt-3">{claimError}</div>}
          </div>
        )}

        {/* Branch 2: not logged in / not a cardholder */}
        {!isCardholder && (
          <div className="mt-4 bg-white border-2 border-black p-4 space-y-3" data-testid="claim-as-guest">
            <div>
              <div className="font-display font-black text-lg">Already have a Curb Crew card?</div>
              <p className="text-sm text-neutral-700 mt-1">Log in to your dashboard and then come back here to claim this sticker.</p>
              <Link to={`/cardholder/login?next=${encodeURIComponent(`/a/${asset.asset_code}`)}`} className="mt-2 inline-flex items-center gap-1 label-tag px-3 py-2 border-2 border-black bg-[color:var(--cc-yellow)] brutal-btn text-xs" data-testid="claim-login-link">
                Log in to claim →
              </Link>
            </div>
            <div className="border-t border-dashed border-neutral-400 pt-3">
              <div className="font-display font-black text-lg">No card yet?</div>
              <p className="text-sm text-neutral-700 mt-1">Asset stickers only work with a main Curb Crew card. You can pick one up wherever you bought the sticker pack.</p>
              <Link to="/" className="mt-2 inline-flex items-center gap-1 label-tag px-3 py-2 border-2 border-black bg-white brutal-btn text-xs">
                Learn more about Curb Crew →
              </Link>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/" className="label-tag text-neutral-500 inline-flex items-center gap-1">
            <ArrowLeft size={12} weight="bold" /> Curb Crew
          </Link>
        </div>
      </div>
    </div>
  );
}


// Fault report form — kept as its own component so the Hustle branch
// at the parent can swap it out cleanly. Includes the photo upload step.
function FaultReportForm({ submit, cardholder, asset, reporterName, setReporterName, reporterPhone, setReporterPhone, description, setDescription, photoUrl, setPhotoUrl, photoUploading, uploadPhoto, submitting, error }) {
  return (
    <form onSubmit={submit} className="mt-4 bg-white border-2 border-black p-4" data-testid="asset-fault-form">
      <div className="flex items-center gap-2 mb-2">
        <Wrench size={18} weight="bold" />
        <div className="font-display font-black text-lg">Report a problem</div>
      </div>
      <p className="text-xs text-neutral-700 mb-3">{cardholder.full_name.split(" ")[0]} will get an email + your number so they can quote or schedule a callout.</p>

      {/* Self-help link — for an unsure homeowner, before they even fill
          the form. Free Google search (Google's AI Overview surfaces
          automatically) so they can see if it's something they can fix
          themselves. Costs nothing per click. */}
      <div className="mb-3 bg-[color:var(--cc-bg-2)] border border-dashed border-black p-3 text-xs">
        <div className="label-tag mb-2">Not sure how big the problem is?</div>
        <DiyChecksButton item={asset?.item} description={description} testid="asset-diy-checks-before" variant="ghost" />
        <div className="text-[11px] text-neutral-600 mt-2">Opens Google AI in a new tab. Then come back and report — or not.</div>
      </div>

      <div className="space-y-2">
        <div>
          <label className="label-tag">Your name</label>
          <input
            value={reporterName}
            onChange={(e) => setReporterName(e.target.value)}
            placeholder="Mrs Smith"
            className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm"
            data-testid="asset-fault-name"
          />
        </div>
        <div>
          <label className="label-tag">Your phone</label>
          <input
            value={reporterPhone}
            onChange={(e) => setReporterPhone(e.target.value.replace(/[^\d+]/g, ""))}
            placeholder="0821234567"
            inputMode="numeric"
            className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm font-mono-cc"
            data-testid="asset-fault-phone"
          />
        </div>
        <div>
          <label className="label-tag">What's wrong? <span className="text-neutral-500">({description.length}/600)</span></label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 600))}
            placeholder="Geyser is leaking from the bottom. Water all over the kitchen floor."
            rows={4}
            className="mt-1 w-full px-3 py-2 border border-black outline-none text-sm resize-none"
            data-testid="asset-fault-description"
          />
        </div>
        {/* Photo upload — optional but plumbers / aircon techs need it. */}
        <div>
          <label className="label-tag">Photo of the fault <span className="text-neutral-500">(optional)</span></label>
          {photoUrl ? (
            <div className="mt-1 flex items-start gap-2" data-testid="asset-fault-photo-preview">
              <img
                src={photoUrl.startsWith("http") ? photoUrl : `${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
                alt="Fault"
                className="w-24 h-24 object-cover border-2 border-black"
              />
              <button
                type="button"
                onClick={() => setPhotoUrl("")}
                className="label-tag px-2 py-1 border border-black bg-white brutal-btn text-xs"
                data-testid="asset-fault-photo-remove"
              >
                <XIcon size={12} weight="bold" className="inline mr-1" /> Remove
              </button>
            </div>
          ) : (
            <label className={`mt-1 flex items-center gap-2 px-3 py-2 border-2 border-dashed border-neutral-500 cursor-pointer text-sm ${photoUploading ? "opacity-50" : ""}`} data-testid="asset-fault-photo-input">
              <Camera size={16} weight="bold" />
              <span>{photoUploading ? "Uploading…" : "Add a photo (helps with quoting)"}</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                disabled={photoUploading}
                className="hidden"
              />
            </label>
          )}
        </div>
        {error && <div className="label-tag text-red-600">{error}</div>}
        <button
          type="submit"
          disabled={submitting || photoUploading}
          className="w-full label-tag px-4 py-3 bg-black text-white border-2 border-black brutal-btn disabled:opacity-50"
          data-testid="asset-fault-submit"
        >{submitting ? "Sending…" : "Send report"}</button>
      </div>
    </form>
  );
}

// Tip jar shown on asset stickers that belong to a Hustle cardholder.
// Reuses the Quick-Pay chip mechanics from the main public profile so the
// experience is identical: tap a chip → cell + amount copied to clipboard,
// paste into your bank's PayShap screen.
function AssetTipJar({ cardholder }) {
  const [flash, setFlash] = React.useState(null);
  const firstName = (cardholder.full_name || "this cardholder").split(" ")[0];
  const digits = String(cardholder.pay_phone || "").replace(/\D/g, "");
  const pretty = digits.startsWith("0") && digits.length === 10
    ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
    : digits;
  const tipAmounts = [5, 10, 20, 30, 40];
  const copy = async (amt) => {
    const payload = amt ? `${digits}\nR${amt}` : digits;
    try {
      await navigator.clipboard.writeText(payload);
      setFlash(amt ?? "num");
      setTimeout(() => setFlash(null), 1400);
    } catch { /* no clipboard */ }
  };
  return (
    <div className="mt-4 bg-white border-2 border-black p-4" data-testid="asset-tip-jar">
      <div className="flex items-center gap-2 mb-2">
        <Bank size={18} weight="bold" />
        <div className="font-display font-black text-lg">Tip or pay {firstName}</div>
      </div>
      <div className="text-xs text-neutral-700 mb-3">PayShap routes by cell number. Tap an amount → paste into your bank's PayShap screen.</div>
      <div className="flex items-center gap-2 mb-3">
        <code className="flex-1 px-3 py-2 bg-[color:var(--cc-bg-2)] border-2 border-black font-mono-cc text-base">{pretty}</code>
        <button onClick={() => copy(null)} className={`label-tag px-3 py-2 border-2 border-black brutal-btn ${flash === "num" ? "bg-green-500" : "bg-white"}`} data-testid="asset-tip-copy-number">
          {flash === "num" ? "Copied" : "Copy"}
        </button>
      </div>
      {cardholder.pay_bank && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-[color:var(--cc-bg-2)] border border-black text-xs">
          <Bank size={14} weight="bold" />
          <span><strong>{firstName}'s PayShap bank:</strong> {cardholder.pay_bank}</span>
        </div>
      )}
      {cardholder.pay_tip_enabled !== false && (
        <div className="grid grid-cols-5 gap-2">
          {tipAmounts.map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => copy(amt)}
              className={`label-tag border-2 border-black brutal-btn px-1 py-3 text-sm font-display font-black tracking-tight transition-colors ${flash === amt ? "bg-green-500 text-black" : "bg-[color:var(--cc-yellow)]"}`}
              data-testid={`asset-tip-${amt}`}
            >
              {flash === amt ? "✓" : `R${amt}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// HomeownerCTA — the "I own this thing" interaction on the public asset
// page. Three states based on the viewer:
//   1. Anonymous visitor → small dimmed link to log in if they want to claim.
//   2. Cardholder who is NOT the homeowner AND no homeowner is set → bright
//      "I'm the homeowner — claim it" button.
//   3. Cardholder who IS the homeowner → "Swap service provider" form.
//      This is the killer feature: the plumber retires, the homeowner
//      types a new artisan's card code and the sticker is repointed in
//      one click.
function HomeownerCTA({ asset, homeowner, cardholder, user }) {
  const [cards, setCards] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [ok, setOk] = React.useState("");
  const [newArtisanCode, setNewArtisanCode] = React.useState("");
  const [showSwap, setShowSwap] = React.useState(false);
  const isCardholder = !!user && user.role === "cardholder";

  React.useEffect(() => {
    if (!isCardholder) return;
    (async () => {
      try {
        const { data } = await api.get("/cardholder/cards");
        setCards((data.cards || []).filter((c) => c.is_active !== false && !c.lost_reported));
      } catch { /* silent */ }
    })();
  }, [isCardholder]);

  // The set of card ids THIS user has — used to decide if they're already the homeowner or the artisan.
  const myCardIds = new Set(cards.map((c) => c.id));
  const iAmTheArtisan = myCardIds.has(cardholder.id);
  const iAmTheHomeowner = !!homeowner && myCardIds.has(homeowner.id);
  const myHomeownerCard = cards.find((c) => c.id === (homeowner && homeowner.id));

  const claim = async (chId) => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.post(`/cardholder/cards/${chId}/assets/claim-as-owner`, { asset_code: asset.asset_code });
      setOk("You now own this asset — refresh to see it in your dashboard.");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const swap = async (e) => {
    e.preventDefault();
    if (!myHomeownerCard || !newArtisanCode.trim()) return;
    setBusy(true); setErr(""); setOk("");
    try {
      const { data } = await api.patch(
        `/cardholder/cards/${myHomeownerCard.id}/assets/${asset.id}/swap-artisan`,
        { new_artisan_card_code: newArtisanCode.trim() },
      );
      setOk(`Service provider swapped to ${data.new_artisan.full_name}. Refreshing…`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  // Anonymous visitor — gentle nudge, don't shout at them.
  if (!isCardholder) {
    if (homeowner) return null; // already owned, nothing to do
    return (
      <div className="mt-3 text-[11px] text-neutral-600 text-center" data-testid="homeowner-cta-guest">
        Own this {asset.item || "asset"}? <Link to={`/cardholder/login?next=${encodeURIComponent(`/a/${asset.asset_code}`)}`} className="underline">Log in</Link> to link it to your card.
      </div>
    );
  }

  // Logged-in artisan looking at their own asset — show them how to attach a homeowner from the dashboard.
  if (iAmTheArtisan) return null;

  // Logged-in homeowner — offer the swap form.
  if (iAmTheHomeowner) {
    return (
      <div className="mt-3 bg-[color:var(--cc-bg-2)] border-2 border-black p-3" data-testid="homeowner-cta-owner">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="label-tag">You own this asset</div>
            <div className="text-[11px] text-neutral-700">If {cardholder.full_name.split(" ")[0]} retires or you want a different service provider, swap them out here.</div>
          </div>
          {!showSwap && <button onClick={() => setShowSwap(true)} className="label-tag px-3 py-1 border-2 border-black bg-white brutal-btn text-xs" data-testid="homeowner-swap-btn">Swap service provider</button>}
        </div>
        {showSwap && (
          <form onSubmit={swap} className="mt-3 space-y-2">
            <label className="label-tag">New service provider's card code</label>
            <input
              value={newArtisanCode}
              onChange={(e) => setNewArtisanCode(e.target.value.toUpperCase().slice(0, 12))}
              placeholder="8-character code from their Curb Crew card"
              className="w-full px-3 py-2 border-2 border-black outline-none text-sm font-mono-cc tracking-wider"
              data-testid="homeowner-swap-input"
            />
            <div className="flex gap-2">
              <button type="submit" disabled={busy || !newArtisanCode.trim()} className="label-tag px-4 py-2 bg-black text-white border-2 border-black brutal-btn text-xs disabled:opacity-50" data-testid="homeowner-swap-submit">{busy ? "Swapping…" : "Swap"}</button>
              <button type="button" onClick={() => { setShowSwap(false); setNewArtisanCode(""); setErr(""); }} className="label-tag px-4 py-2 bg-white border-2 border-black brutal-btn text-xs">Cancel</button>
            </div>
            {err && <div className="label-tag text-red-600">{err}</div>}
            {ok && <div className="label-tag text-green-700">{ok}</div>}
          </form>
        )}
      </div>
    );
  }

  // Logged-in cardholder, asset has NO homeowner yet, they're not the artisan — they can claim it.
  if (!homeowner) {
    return (
      <div className="mt-3 bg-[color:var(--cc-yellow)] border-2 border-black p-3" data-testid="homeowner-cta-claim">
        <div className="font-display font-black text-base">Own this {asset.item ? asset.item.toLowerCase() : "asset"}?</div>
        <div className="text-[11px] text-neutral-800 mt-0.5">Link it to your card so you can swap {cardholder.full_name.split(" ")[0]} for a different service provider in the future.</div>
        <div className="mt-2 space-y-1.5">
          {cards.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={busy}
              onClick={() => claim(c.id)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 border-2 border-black bg-white brutal-btn text-left disabled:opacity-50"
              data-testid={`homeowner-claim-${c.code}`}
            >
              <div className="font-display font-black text-sm truncate">{c.full_name} <span className="font-mono-cc text-[10px] text-neutral-500">#{c.code}</span></div>
              <div className="label-tag text-xs">Claim →</div>
            </button>
          ))}
        </div>
        {err && <div className="label-tag text-red-600 mt-2">{err}</div>}
        {ok && <div className="label-tag text-green-700 mt-2">{ok}</div>}
      </div>
    );
  }

  // Asset already has a different homeowner — show a small "this is owned by X" notice (no actions).
  return null;
}
